import React, { useEffect, useState, useCallback } from 'react';
import { targetAPI, userAPI } from '../api';
import { toast } from 'sonner';
import { Plus, Trash2, Target, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { getTargetMonthRange, localDateBoundaryIso } from '../lib/dateRanges';

const TARGET_TYPES = [
  { v: 'calls', l: 'Calls' },
  { v: 'connected_calls', l: 'Connected Calls' },
  { v: 'talk_time_minutes', l: 'Talk Time (min)' },
  { v: 'meetings', l: 'Meetings' },
  { v: 'follow_ups', l: 'Follow-ups' },
  { v: 'site_visits', l: 'Site Visits' },
  { v: 'quotations', l: 'Quotations' },
  { v: 'sales', l: 'Sales (Deals Closed)' },
  { v: 'revenue', l: 'Revenue (₹)' },
  { v: 'conversion_rate', l: 'Lead Conversion Rate (%)' }
];
const PERIOD_TYPES = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

export const SalesTargetsPage = () => {
  const { user, hasPermission } = useAuth();
  // Target authority is permission driven (targets.manage), never a role-name check.
  const canManage = user?.role === 'super_admin' || !!hasPermission?.('targets.manage');
  const [targets, setTargets] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const range = getTargetMonthRange();
  const [form, setForm] = useState({
    user_id: '', period_type: 'monthly',
    period_start: range.start, period_end: range.end,
    target_type: 'sales', target_value: 10
  });

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, uRes] = await Promise.all([
        targetAPI.list(),
        userAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setTargets(tRes.data || []);
      setUsers(uRes.data || []);
      // Fetch progress for each
      const pMap = {};
      await Promise.all((tRes.data || []).map(async (t) => {
        try { const { data } = await targetAPI.progress(t._id || t.id); pMap[t._id || t.id] = data; } catch {}
      }));
      setProgressMap(pMap);
    } catch { toast.error('Failed to load targets'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await targetAPI.create({
        ...form,
        target_value: parseFloat(form.target_value),
        period_start: localDateBoundaryIso(form.period_start),
        period_end: localDateBoundaryIso(form.period_end, true)
      });
      toast.success('Target created');
      setShowForm(false);
      fetchTargets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    try { await targetAPI.delete(id); toast.success('Deleted'); fetchTargets(); }
    catch { toast.error('Failed'); }
  };

  const userName = (uid) => users.find(u => u.id === uid)?.name || uid;

  return (
    <div className="space-y-6" data-testid="targets-container">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-outfit font-semibold">Sales Targets</h1>
          <p className="text-muted-foreground">Monthly targets, achievement and progress per user</p>
        </div>
        {canManage && (
          <button onClick={() => setShowForm(!showForm)} data-testid="new-target-btn" className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm">
            <Plus className="h-4 w-4" /> New Target
          </button>
        )}
      </div>

      {showForm && canManage && (
        <form onSubmit={submit} className="clay-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="target-form">
          <div>
            <label className="text-xs text-muted-foreground">User</label>
            <select required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
              <option value="">Select user</option>
              {users.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role.replace('_', ' ')})</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Period Type</label>
            <select value={form.period_type} onChange={(e) => setForm({ ...form, period_type: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
              {PERIOD_TYPES.map(p => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Target Type</label>
            <select value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm">
              {TARGET_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Start Date</label>
            <input type="date" required value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">End Date</label>
            <input type="date" required value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Target Value{form.target_type === 'conversion_rate' ? ' (%)' : ''}</label>
            <input type="number" required min="0" max={form.target_type === 'conversion_rate' ? 100 : undefined} value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} data-testid="target-value-input" className="w-full mt-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" />
          </div>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
            <button type="submit" data-testid="target-submit-btn" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Save</button>
          </div>
        </form>
      )}

      {loading ? <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div></div> :
       targets.length === 0 ? <div className="clay-card p-8 text-center text-sm text-muted-foreground">No targets yet.</div> :
       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
         {targets.map(t => {
           const tid = t._id || t.id;
           const p = progressMap[tid] || { achieved: 0, achievement_pct: 0, remaining: t.target_value };
           const isMoney = t.target_type === 'revenue';
           const isPercentage = t.target_type === 'conversion_rate';
           const fmt = (n) => {
             const value = Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 });
             if (isMoney) return `₹${value}`;
             if (isPercentage) return `${value}%`;
             return value;
           };
           return (
             <div key={tid} data-testid={`target-card-${tid}`} className="clay-card p-5">
               <div className="flex items-start justify-between mb-3">
                 <div>
                   <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                     <Target className="h-3 w-3" /> {t.period_type} · {TARGET_TYPES.find(x => x.v === t.target_type)?.l}
                   </div>
                   <p className="font-medium">{userName(t.user_id)}</p>
                   <p className="text-xs text-muted-foreground">
                     {format(new Date(t.period_start), 'MMM d')} → {format(new Date(t.period_end), 'MMM d, yyyy')}
                   </p>
                 </div>
                 {canManage && (
                   <button onClick={() => remove(tid)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" data-testid={`delete-target-${tid}`}>
                     <Trash2 className="h-4 w-4" />
                   </button>
                 )}
               </div>
               <div className="mb-2 flex justify-between text-sm">
                 <span className="text-muted-foreground">Achieved</span>
                 <span className="font-semibold">{fmt(p.achieved)} / {fmt(t.target_value)}</span>
               </div>
               <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                 <div className={`h-full rounded-full transition-all ${p.achievement_pct >= 100 ? 'bg-green-500' : p.achievement_pct >= 60 ? 'bg-primary' : 'bg-amber-500'}`}
                      style={{ width: `${Math.min(p.achievement_pct, 100)}%` }} />
               </div>
               <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                 <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {p.achievement_pct}% complete</span>
                 <span>Remaining: {fmt(p.remaining)}</span>
              </div>
              {(p.days_remaining != null) && (
                <div className={`mt-2 text-xs rounded-lg px-2 py-1.5 ${p.at_risk ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800' : 'bg-slate-50 dark:bg-slate-800/50 text-muted-foreground'}`} data-testid={`target-risk-${tid}`}>
                  {p.at_risk
                    ? `At Risk — ${p.risk_reason || 'Target unlikely at current run rate'} · ${p.days_remaining}d left${p.required_run_rate ? `, need ${fmt(p.required_run_rate)}/day` : ''}`
                    : `${p.days_remaining}d left${p.required_run_rate ? ` · run-rate ${fmt(p.required_run_rate)}/day` : ''}`}
                </div>
              )}
            </div>
          );
        })}
       </div>}
    </div>
  );
};
export default SalesTargetsPage;
